import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Button, Input, TextArea } from '@heroui/react'
import { api, ApiError } from '../lib/api'
import { certificateState } from '../lib/sso'
import type {
  SAMLCertificate,
  SAMLDraft,
  SAMLFound,
  SAMLServiceProvider,
  SAMLSettings,
  SSODraft,
  SSOEndpoints,
  SSOProtocol,
  SSOSettings,
} from '../lib/types'
import { Icon } from '../ui/icons'
import { EmptyState, StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { Mark, Masked, TestResult } from './connectParts'
import { Toggle, useFlash } from './sectionKit'
import { useKerberos, WINDOWS, WindowsSignInCard, WindowsSignInPage } from './WindowsSignIn'
import {
  ChoiceSelect,
  EDIT,
  InnerPage,
  Notice,
  PageHead,
  Seg,
  SettingBlock,
  SettingRow,
  SettingsCard,
  TagList,
  useOpenRecord,
  Values,
} from './settingsKit'
import '../styles/connect.css'

/**
 * Single sign-on: the organisation's identity provider, OpenID Connect (Entra ID,
 * ADFS, Keycloak) or SAML 2.0, one of them on at a time. The installation owner
 * sets it, and in the cloud the organisation's administrator; otherwise they read it. Who a group makes what is
 * the Sign-in directory's group list, shared with LDAP.
 */

const BLANK: SSODraft = {
  displayName: '',
  issuer: '',
  clientId: '',
  scopes: 'openid profile email',
  usernameClaim: 'preferred_username',
  nameClaim: 'name',
  emailClaim: 'email',
  groupsClaim: 'groups',
  emailDomains: [],
  createAccounts: false,
  enabled: false,
}

const SAML_BLANK: SAMLDraft = {
  displayName: '',
  metadataUrl: '',
  entityId: '',
  ssoUrl: '',
  sloUrl: '',
  nameIdFormat: '',
  usernameAttribute: '',
  nameAttribute: 'name',
  emailAttribute: 'email',
  groupsAttribute: 'groups',
  emailDomains: [],
  clockSkewSeconds: 120,
  allowIdpInitiated: false,
  createAccounts: false,
  enabled: false,
}

const NAME_ID_FORMATS = [
  { value: 'any', label: 'Any' },
  { value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent', label: 'Persistent' },
  { value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress', label: 'Email address' },
  { value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified', label: 'Unspecified' },
  { value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient', label: 'Transient' },
]

const PROTOCOLS = [
  { value: 'oidc' as const, label: 'OpenID Connect' },
  { value: 'saml' as const, label: 'SAML 2.0' },
]

const draftOf = (s: SSOSettings): SSODraft => ({
  displayName: s.displayName,
  issuer: s.issuer,
  clientId: s.clientId,
  scopes: s.scopes,
  usernameClaim: s.usernameClaim,
  nameClaim: s.nameClaim,
  emailClaim: s.emailClaim,
  groupsClaim: s.groupsClaim,
  emailDomains: s.emailDomains ?? [],
  createAccounts: s.createAccounts,
  enabled: s.enabled,
})

const samlDraftOf = (s: SAMLSettings): SAMLDraft => ({
  displayName: s.displayName,
  metadataUrl: s.metadataUrl,
  entityId: s.entityId,
  ssoUrl: s.ssoUrl,
  sloUrl: s.sloUrl,
  nameIdFormat: s.nameIdFormat,
  usernameAttribute: s.usernameAttribute,
  nameAttribute: s.nameAttribute,
  emailAttribute: s.emailAttribute,
  groupsAttribute: s.groupsAttribute,
  emailDomains: s.emailDomains ?? [],
  clockSkewSeconds: s.clockSkewSeconds,
  allowIdpInitiated: s.allowIdpInitiated,
  createAccounts: s.createAccounts,
  enabled: s.enabled,
})

const refusal = (err: unknown) => (err instanceof ApiError ? err.message : 'The server could not be reached.')
const day = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
const mono = (s: string) => <span className="connect-mono">{s}</span>

export function SingleSignOn({ organisation, canConnect }: { organisation?: string; canConnect: boolean }) {
  const { say } = useStaffText()
  const { flash, toast } = useFlash()
  const [saved, setSaved] = useState<SSOSettings | null>(null)
  const [saml, setSaml] = useState<SAMLSettings | null>(null)
  const [samlSp, setSamlSp] = useState<SAMLServiceProvider | null>(null)
  const [protocol, setProtocol] = useState<SSOProtocol>('oidc')
  const [redirectUri, setRedirectUri] = useState('')
  const [centralOnly, setCentralOnly] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const rec = useOpenRecord([], () => '', loaded, canConnect && !centralOnly ? [EDIT, WINDOWS] : [])
  const editing = canConnect && !centralOnly && rec.code === EDIT && loaded
  const [editAs, setEditAs] = useState<SSOProtocol>('oidc')
  const windows = useKerberos(organisation)

  const load = useCallback(async () => {
    try {
      const r = await api.sso(organisation)
      setSaved(r.sso)
      setSaml(r.saml)
      setSamlSp(r.samlSp)
      setProtocol(r.protocol || 'oidc')
      setRedirectUri(r.redirectUri)
      setCentralOnly(r.centralOnly)
      setLoaded(true)
    } catch (err) {
      setError(refusal(err))
    }
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  const shown = protocol === 'saml' ? saml : saved
  const head = (
    <PageHead
      title={say('admin.section.sso')}
      line="Staff sign in with your identity provider."
      actions={
        loaded &&
        canConnect &&
        !centralOnly && (
          <Button
            variant={shown ? 'secondary' : 'primary'}
            onPress={() => {
              setEditAs(protocol)
              rec.nav.open(EDIT)
            }}
          >
            {shown ? (
              <>
                <Icon name="edit" size={15} /> Edit
              </>
            ) : (
              'Set up'
            )}
          </Button>
        )
      }
    />
  )

  if (editing) {
    const done = async () => {
      await load()
      rec.close()
      flash('Single sign-on saved')
    }
    const choose = <Seg label="Protocol" value={editAs} onChange={setEditAs} options={PROTOCOLS} />
    return (
      <>
        {editAs === 'saml' ? (
          <SAMLPage organisation={organisation} saved={saml} protocolChoice={choose} onClose={rec.close} onSaved={done} />
        ) : (
          <ProviderPage
            organisation={organisation}
            saved={saved}
            redirectUri={redirectUri}
            protocolChoice={choose}
            onClose={rec.close}
            onSaved={done}
          />
        )}
        {toast}
      </>
    )
  }

  if (canConnect && !centralOnly && rec.code === WINDOWS && loaded) {
    return (
      <>
        <WindowsSignInPage
          organisation={organisation}
          saved={windows.saved}
          back={say('admin.section.sso')}
          onClose={rec.close}
          onSaved={async () => {
            await windows.load()
            rec.close()
            flash('Windows sign-in saved')
          }}
        />
        {toast}
      </>
    )
  }

  if (!loaded) {
    return (
      <>
        {head}
        {error && <Notice>{error}</Notice>}
      </>
    )
  }

  return (
    <>
      {head}
      {centralOnly && <Notice tone="accent">Set up at the central server.</Notice>}
      <SettingsCard
        title="Identity provider"
        aside={
          <>
            {!canConnect && (
              <span className="dir-note">
                <Mark name="lock" size={14} /> {say('connect.ownerOnly')}
              </span>
            )}
            <StatusChip tone={shown?.enabled ? 'ok' : undefined}>{!shown ? 'Not set up' : shown.enabled ? 'On' : 'Off'}</StatusChip>
          </>
        }
      >
        {!shown && <EmptyState icon={<Icon name="key" size={20} />} title="No provider yet" />}
        {shown && <SettingRow label="Protocol">{protocol === 'saml' ? 'SAML 2.0' : 'OpenID Connect'}</SettingRow>}
        {protocol === 'saml' && saml && <SAMLRows saml={saml} />}
        {protocol === 'oidc' && saved?.clientSecretUnreadable && (
          <SettingBlock>
            <Notice tone="warning">Client secret unreadable on this server. Enter it again.</Notice>
          </SettingBlock>
        )}
        {protocol === 'oidc' && saved && <OIDCRows saved={saved} />}
        {protocol === 'oidc' && (
          <SettingRow label="Redirect URI" description="Register it at the provider.">
            <span className="connect-mono">{redirectUri}</span>
          </SettingRow>
        )}
      </SettingsCard>

      {protocol === 'saml' && samlSp && <OurSide sp={samlSp} />}
      {shown && canConnect && !centralOnly && (protocol === 'saml' ? <SAMLTest organisation={organisation} /> : <Discovery organisation={organisation} />)}
      <WindowsSignInCard
        organisation={organisation}
        saved={windows.saved}
        error={windows.error}
        canEdit={canConnect && !centralOnly}
        onEdit={() => rec.nav.open(WINDOWS)}
      />
      {toast}
    </>
  )
}

function OIDCRows({ saved }: { saved: SSOSettings }) {
  const { say } = useStaffText()
  return (
    <>
      <SettingRow label="Button" description="On the sign-in page.">
        Sign in with {saved.displayName}
      </SettingRow>
      <SettingRow label="Provider">
        <Values
          items={[
            { label: 'Issuer', value: mono(saved.issuer) },
            { label: 'Client ID', value: mono(saved.clientId) },
            { label: 'Client secret', value: saved.clientSecretSet ? <Masked /> : <span className="set-muted">None (public client)</span> },
            { label: 'Scopes', value: mono(saved.scopes) },
          ]}
        />
      </SettingRow>
      <SettingRow label="Claims">
        <Values
          items={[
            { label: 'Username', value: mono(saved.usernameClaim) },
            { label: 'Name', value: mono(saved.nameClaim) },
            { label: 'Email', value: mono(saved.emailClaim) },
            {
              label: 'Groups',
              value: saved.groupsClaim ? mono(saved.groupsClaim) : <span className="set-muted">Roles set by hand</span>,
            },
          ]}
        />
      </SettingRow>
      <SettingRow label="Who may sign in">
        <Values
          items={[
            { label: 'First sign-in', value: saved.createAccounts ? say('connect.createsAccount') : say('connect.existingOnly') },
            {
              label: 'Email domains',
              value: (saved.emailDomains ?? []).length > 0 ? (saved.emailDomains ?? []).map((d) => `@${d}`).join(', ') : 'Any',
            },
          ]}
        />
      </SettingRow>
    </>
  )
}

/** A certificate with its expiry, flagged 30 days ahead. */
function Cert({ c }: { c: SAMLCertificate }) {
  const state = certificateState(c.notAfter)
  return (
    <span className="connect-mono" title={c.sha256}>
      {c.subject} · {day(c.notAfter)}{' '}
      {state !== 'ok' && <StatusChip tone={state === 'expired' ? 'bad' : 'warn'}>{state === 'expired' ? 'Expired' : 'Expires soon'}</StatusChip>}
    </span>
  )
}

function CertList({ certs }: { certs: SAMLCertificate[] }) {
  if (certs.length === 0) return <span className="set-muted">None</span>
  return (
    <span className="connect-certs">
      {certs.map((c) => (
        <Cert key={c.sha256} c={c} />
      ))}
    </span>
  )
}

function SAMLRows({ saml }: { saml: SAMLSettings }) {
  const { say } = useStaffText()
  // The provider signs with one; warn when the last good one is going.
  const latest = saml.certificates.reduce<SAMLCertificate | null>((a, c) => (!a || c.notAfter > a.notAfter ? c : a), null)
  const state = latest ? certificateState(latest.notAfter) : 'ok'
  const format = NAME_ID_FORMATS.find((f) => f.value === (saml.nameIdFormat || 'any'))?.label ?? saml.nameIdFormat
  return (
    <>
      {saml.spKeyUnreadable && (
        <SettingBlock>
          <Notice tone="warning">Our SAML key is unreadable here. Save to make a new one.</Notice>
        </SettingBlock>
      )}
      {latest && state !== 'ok' && (
        <SettingBlock>
          <Notice tone={state === 'expired' ? 'danger' : 'warning'}>
            {state === 'expired' ? 'Signing certificate expired.' : `Signing certificate expires ${day(latest.notAfter)}.`}
          </Notice>
        </SettingBlock>
      )}
      <SettingRow label="Button" description="On the sign-in page.">
        Sign in with {saml.displayName}
      </SettingRow>
      <SettingRow label="Provider">
        <Values
          items={[
            { label: 'Entity ID', value: saml.entityId ? mono(saml.entityId) : <span className="set-muted">Not given</span> },
            { label: 'Sign-in', value: saml.ssoUrl ? mono(saml.ssoUrl) : <span className="set-muted">Not given</span> },
            { label: 'Sign-out', value: saml.sloUrl ? mono(saml.sloUrl) : <span className="set-muted">None</span> },
            {
              label: 'Metadata',
              value: saml.metadataUrl ? (
                <span>
                  {mono(saml.metadataUrl)}
                  {saml.metadataFetchedAt && <span className="set-muted"> · read {day(saml.metadataFetchedAt)}</span>}
                </span>
              ) : (
                <span className="set-muted">Uploaded or typed</span>
              ),
            },
          ]}
        />
      </SettingRow>
      <SettingRow label="Certificates">
        <CertList certs={saml.certificates} />
      </SettingRow>
      <SettingRow label="Attributes">
        <Values
          items={[
            { label: 'Username', value: saml.usernameAttribute ? mono(saml.usernameAttribute) : <span className="set-muted">NameID</span> },
            { label: 'Name', value: saml.nameAttribute ? mono(saml.nameAttribute) : <span className="set-muted">None</span> },
            { label: 'Email', value: saml.emailAttribute ? mono(saml.emailAttribute) : <span className="set-muted">None</span> },
            {
              label: 'Groups',
              value: saml.groupsAttribute ? mono(saml.groupsAttribute) : <span className="set-muted">Roles set by hand</span>,
            },
          ]}
        />
      </SettingRow>
      <SettingRow label="Who may sign in">
        <Values
          items={[
            { label: 'First sign-in', value: saml.createAccounts ? say('connect.createsAccount') : say('connect.existingOnly') },
            {
              label: 'Email domains',
              value: (saml.emailDomains ?? []).length > 0 ? (saml.emailDomains ?? []).map((d) => `@${d}`).join(', ') : 'Any',
            },
            { label: 'Provider-initiated', value: saml.allowIdpInitiated ? 'Allowed' : 'Off' },
          ]}
        />
      </SettingRow>
      <SettingRow label="Advanced">
        <Values
          items={[
            { label: 'NameID format', value: format },
            { label: 'Clock skew', value: `${saml.clockSkewSeconds} s` },
          ]}
        />
      </SettingRow>
    </>
  )
}

/** Our side, for the provider's administrator to import. */
function OurSide({ sp }: { sp: SAMLServiceProvider }) {
  return (
    <SettingsCard title="For your provider" line="Import our metadata there.">
      {!sp.ready && (
        <SettingBlock>
          <Notice tone="accent">Save once to make our metadata.</Notice>
        </SettingBlock>
      )}
      <SettingRow label="Metadata">
        {sp.ready ? (
          <span>
            {mono(sp.metadataUrl)}{' '}
            <a href={`${sp.metadataUrl}?download=1`} className="linkish">
              Download
            </a>
          </span>
        ) : (
          mono(sp.metadataUrl)
        )}
      </SettingRow>
      <SettingRow label="Entity ID">{mono(sp.entityId)}</SettingRow>
      <SettingRow label="Reply URL" description="Assertion Consumer Service, HTTP-POST.">
        {mono(sp.acsUrl)}
      </SettingRow>
      <SettingRow label="Logout URL" description="Single Logout, HTTP-Redirect or HTTP-POST.">
        {mono(sp.sloUrl)}
      </SettingRow>
    </SettingsCard>
  )
}

function ProviderPage({
  organisation,
  saved,
  redirectUri,
  protocolChoice,
  onClose,
  onSaved,
}: {
  organisation?: string
  saved: SSOSettings | null
  redirectUri: string
  protocolChoice: ReactNode
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const { say } = useStaffText()
  const [draft, setDraft] = useState<SSODraft>(saved ? draftOf(saved) : BLANK)
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const set = (patch: Partial<SSODraft>) => setDraft((d) => ({ ...d, ...patch }))

  // The server refuses to keep a secret across a new issuer or client.
  const secretAgain = Boolean(saved?.clientSecretSet) && (draft.issuer.trim() !== saved?.issuer || draft.clientId.trim() !== saved?.clientId)
  const valid = draft.displayName.trim() !== '' && draft.issuer.trim() !== '' && draft.clientId.trim() !== '' && (!secretAgain || secret !== '')

  async function save() {
    if (busy || !valid) return
    setBusy(true)
    setError('')
    try {
      await api.saveSSO({
        organisation,
        ...draft,
        displayName: draft.displayName.trim(),
        issuer: draft.issuer.trim(),
        clientId: draft.clientId.trim(),
        // Absent keeps the stored secret; only a typed one replaces it.
        ...(secret ? { clientSecret: secret } : {}),
      })
      await onSaved()
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      back={say('admin.section.sso')}
      title={saved ? 'Identity provider' : 'Set up single sign-on'}
      onClose={onClose}
      onSubmit={() => void save()}
      submitLabel="Save"
      valid={valid}
      busy={busy}
    >
      {error && <Notice>{error}</Notice>}
      {protocolChoice}
      <Toggle title="Single sign-on" checked={draft.enabled} onChange={(enabled) => set({ enabled })} />

      <section className="connect-part">
        <h3 className="connect-part__title">Provider</h3>
        <label className="field">
          <span>Button name</span>
          <Input fullWidth maxLength={40} value={draft.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder="Bank login" />
        </label>
        <label className="field">
          <span>Issuer</span>
          <Input
            fullWidth
            className="connect-mono"
            value={draft.issuer}
            onChange={(e) => set({ issuer: e.target.value })}
            placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0"
          />
        </label>
        <label className="field">
          <span>Client ID</span>
          <Input fullWidth className="connect-mono" value={draft.clientId} onChange={(e) => set({ clientId: e.target.value })} />
        </label>
        <label className="field">
          <span>Client secret</span>
          <Input
            fullWidth
            type="password"
            autoComplete="new-password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={saved?.clientSecretSet && !secretAgain ? 'Stored. Leave empty to keep it.' : 'Empty for a public client'}
            aria-invalid={secretAgain && secret === ''}
          />
          {secretAgain && <small>Required: the issuer or client changed.</small>}
        </label>
        <label className="field">
          <span>Scopes</span>
          <Input fullWidth className="connect-mono" value={draft.scopes} onChange={(e) => set({ scopes: e.target.value })} />
        </label>
        <p className="set-muted">
          Redirect URI <span className="connect-mono">{redirectUri}</span>
        </p>
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Claims</h3>
        <div className="pk-fields">
          <label className="field">
            <span>Username</span>
            <Input fullWidth className="connect-mono" value={draft.usernameClaim} onChange={(e) => set({ usernameClaim: e.target.value })} />
          </label>
          <label className="field">
            <span>Name</span>
            <Input fullWidth className="connect-mono" value={draft.nameClaim} onChange={(e) => set({ nameClaim: e.target.value })} />
          </label>
          <label className="field">
            <span>Email</span>
            <Input fullWidth className="connect-mono" value={draft.emailClaim} onChange={(e) => set({ emailClaim: e.target.value })} />
          </label>
          <label className="field">
            <span title="Matched against the Sign-in directory's groups. Empty: roles set by hand.">Groups</span>
            <Input fullWidth className="connect-mono" value={draft.groupsClaim} onChange={(e) => set({ groupsClaim: e.target.value })} placeholder="groups" />
          </label>
        </div>
      </section>

      <WhoMaySignIn
        createAccounts={draft.createAccounts}
        emailDomains={draft.emailDomains}
        onChange={(patch) => set(patch)}
      />
    </InnerPage>
  )
}

function WhoMaySignIn({
  createAccounts,
  emailDomains,
  onChange,
  children,
}: {
  createAccounts: boolean
  emailDomains: string[]
  onChange: (patch: { createAccounts?: boolean; emailDomains?: string[] }) => void
  children?: ReactNode
}) {
  const [domain, setDomain] = useState('')
  const addDomain = () => {
    const typed = domain
      .toLowerCase()
      .split(/[\s,]+/)
      .map((d) => d.replace(/^@/, ''))
      .filter(Boolean)
    onChange({ emailDomains: [...emailDomains, ...typed.filter((d) => !emailDomains.includes(d))] })
    setDomain('')
  }
  return (
    <section className="connect-part">
      <h3 className="connect-part__title">Who may sign in</h3>
      <Toggle
        title="Create accounts"
        hint="On first sign-in, from their groups. Counted against the licence."
        checked={createAccounts}
        onChange={(v) => onChange({ createAccounts: v })}
      />
      <TagList
        label="Email domains"
        items={emailDomains.map((d) => ({ id: d, label: `@${d}` }))}
        onRemove={(d) => onChange({ emailDomains: emailDomains.filter((x) => x !== d) })}
      />
      <div className="connect-chip-add">
        <Input
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="bank.az"
          aria-label="Add an email domain"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              addDomain()
            }
          }}
        />
        <Button variant="secondary" isDisabled={!domain.trim()} onPress={addDomain}>
          <Icon name="plus" size={14} /> Add
        </Button>
      </div>
      {children}
    </section>
  )
}

type Source = 'url' | 'file' | 'manual'

function SAMLPage({
  organisation,
  saved,
  protocolChoice,
  onClose,
  onSaved,
}: {
  organisation?: string
  saved: SAMLSettings | null
  protocolChoice: ReactNode
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const { say } = useStaffText()
  const [draft, setDraft] = useState<SAMLDraft>(saved ? samlDraftOf(saved) : SAML_BLANK)
  const [source, setSource] = useState<Source>(saved && !saved.metadataUrl && saved.entityId ? 'manual' : 'url')
  const [xml, setXml] = useState('')
  const [fileName, setFileName] = useState('')
  const [certs, setCerts] = useState('')
  const [found, setFound] = useState<SAMLFound | null>(null)
  const [reading, setReading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const set = (patch: Partial<SAMLDraft>) => setDraft((d) => ({ ...d, ...patch }))

  // Certificates vouch for one provider: a new entity ID needs them again.
  const certsAgain = source === 'manual' && Boolean(saved?.certificates.length) && draft.entityId.trim() !== saved?.entityId
  const hasProvider =
    source === 'url' ? draft.metadataUrl.trim() !== '' : source === 'file' ? xml !== '' || Boolean(saved?.entityId) : draft.entityId.trim() !== '' && draft.ssoUrl.trim() !== ''
  const valid = draft.displayName.trim() !== '' && (!draft.enabled || hasProvider) && (!certsAgain || certs.trim() !== '')

  async function read(sourceBody: { metadataUrl?: string; metadataXml?: string }) {
    setReading(true)
    setError('')
    setFound(null)
    try {
      setFound((await api.testSAML(organisation, sourceBody)).found)
    } catch (err) {
      setError(refusal(err))
    } finally {
      setReading(false)
    }
  }

  async function pick(file: File | undefined) {
    if (!file) return
    const text = await file.text()
    setXml(text)
    setFileName(file.name)
    await read({ metadataXml: text })
  }

  async function save() {
    if (busy || !valid) return
    setBusy(true)
    setError('')
    try {
      const body = { organisation, ...draft, displayName: draft.displayName.trim() }
      if (source === 'url') {
        body.metadataUrl = draft.metadataUrl.trim()
      } else {
        body.metadataUrl = ''
      }
      await api.saveSAML({
        ...body,
        ...(source === 'file' && xml ? { metadataXml: xml } : {}),
        // Absent keeps the stored certificates; only pasted ones replace them.
        ...(source === 'manual' && certs.trim() ? { certificates: certs } : {}),
      })
      await onSaved()
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      back={say('admin.section.sso')}
      title={saved ? 'Identity provider' : 'Set up single sign-on'}
      onClose={onClose}
      onSubmit={() => void save()}
      submitLabel="Save"
      valid={valid}
      busy={busy}
    >
      {error && <Notice>{error}</Notice>}
      {protocolChoice}
      <Toggle title="Single sign-on" checked={draft.enabled} onChange={(enabled) => set({ enabled })} />

      <section className="connect-part">
        <h3 className="connect-part__title">Provider</h3>
        <label className="field">
          <span>Button name</span>
          <Input fullWidth maxLength={40} value={draft.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder="Bank login" />
        </label>
        <Seg
          label="Provider details"
          value={source}
          onChange={(s) => {
            setSource(s)
            setFound(null)
          }}
          options={[
            { value: 'url', label: 'Metadata URL' },
            { value: 'file', label: 'Metadata file' },
            { value: 'manual', label: 'By hand' },
          ]}
        />
        {source === 'url' && (
          <div className="connect-chip-add">
            <Input
              fullWidth
              className="connect-mono"
              aria-label="Metadata URL"
              value={draft.metadataUrl}
              onChange={(e) => set({ metadataUrl: e.target.value })}
              placeholder="https://adfs.bank.az/FederationMetadata/2007-06/FederationMetadata.xml"
            />
            <Button variant="secondary" isDisabled={reading || !draft.metadataUrl.trim()} onPress={() => void read({ metadataUrl: draft.metadataUrl.trim() })}>
              {reading ? 'Reading…' : 'Read'}
            </Button>
          </div>
        )}
        {source === 'file' && (
          <label className="field">
            <span>{fileName || (saved?.entityId ? 'Stored. Choose a file to replace it.' : 'Metadata XML')}</span>
            <input type="file" accept=".xml,application/xml,text/xml" onChange={(e) => void pick(e.target.files?.[0])} />
          </label>
        )}
        {source === 'manual' && (
          <>
            <label className="field">
              <span>Entity ID</span>
              <Input fullWidth className="connect-mono" value={draft.entityId} onChange={(e) => set({ entityId: e.target.value })} />
            </label>
            <label className="field">
              <span>Sign-in URL</span>
              <Input
                fullWidth
                className="connect-mono"
                value={draft.ssoUrl}
                onChange={(e) => set({ ssoUrl: e.target.value })}
                placeholder="HTTP-Redirect binding"
              />
            </label>
            <label className="field">
              <span>Sign-out URL</span>
              <Input fullWidth className="connect-mono" value={draft.sloUrl} onChange={(e) => set({ sloUrl: e.target.value })} placeholder="Optional" />
            </label>
            <label className="field">
              <span>Signing certificates</span>
              <TextArea
                fullWidth
                className="connect-textarea"
                rows={4}
                spellCheck={false}
                value={certs}
                onChange={(e) => setCerts(e.target.value)}
                placeholder={saved?.certificates.length && !certsAgain ? 'Stored. Leave empty to keep them.' : '-----BEGIN CERTIFICATE-----'}
                aria-invalid={certsAgain && certs.trim() === ''}
              />
              {certsAgain && <small>Required: the entity ID changed.</small>}
            </label>
          </>
        )}
        {found && (
          <TestResult ok title="Metadata read">
            <FoundValues found={found} />
          </TestResult>
        )}
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Attributes</h3>
        <div className="pk-fields">
          <label className="field">
            <span title="Empty: the NameID.">Username</span>
            <Input fullWidth className="connect-mono" value={draft.usernameAttribute} onChange={(e) => set({ usernameAttribute: e.target.value })} placeholder="NameID" />
          </label>
          <label className="field">
            <span>Name</span>
            <Input fullWidth className="connect-mono" value={draft.nameAttribute} onChange={(e) => set({ nameAttribute: e.target.value })} />
          </label>
          <label className="field">
            <span>Email</span>
            <Input fullWidth className="connect-mono" value={draft.emailAttribute} onChange={(e) => set({ emailAttribute: e.target.value })} />
          </label>
          <label className="field">
            <span title="Matched against the Sign-in directory's groups. Empty: roles set by hand.">Groups</span>
            <Input fullWidth className="connect-mono" value={draft.groupsAttribute} onChange={(e) => set({ groupsAttribute: e.target.value })} />
          </label>
        </div>
      </section>

      <WhoMaySignIn createAccounts={draft.createAccounts} emailDomains={draft.emailDomains} onChange={(patch) => set(patch)}>
        <Toggle
          title="Provider-initiated sign-in"
          hint="Off: sign-in starts on this page."
          checked={draft.allowIdpInitiated}
          onChange={(allowIdpInitiated) => set({ allowIdpInitiated })}
        />
      </WhoMaySignIn>

      <section className="connect-part">
        <h3 className="connect-part__title">Advanced</h3>
        <div className="pk-fields">
          <label className="field">
            <span>NameID format</span>
            <ChoiceSelect
              label="NameID format"
              value={draft.nameIdFormat || 'any'}
              onChange={(v) => set({ nameIdFormat: v === 'any' ? '' : v })}
              options={NAME_ID_FORMATS}
            />
          </label>
          <label className="field">
            <span>Clock skew (s)</span>
            <Input
              fullWidth
              type="number"
              min={0}
              max={600}
              value={String(draft.clockSkewSeconds)}
              onChange={(e) => set({ clockSkewSeconds: Math.max(0, Math.min(600, Number(e.target.value) || 0)) })}
            />
          </label>
        </div>
      </section>
    </InnerPage>
  )
}

function FoundValues({ found }: { found: SAMLFound }) {
  return (
    <Values
      items={[
        { label: 'Entity ID', value: mono(found.entityId) },
        { label: 'Sign-in', value: mono(found.ssoUrl) },
        { label: 'Sign-out', value: found.sloUrl ? mono(found.sloUrl) : <span className="set-muted">None</span> },
        { label: 'Certificates', value: <CertList certs={found.certificates} /> },
      ]}
    />
  )
}

/** Test: reads the SAML provider's metadata now. */
function SAMLTest({ organisation }: { organisation?: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [found, setFound] = useState<SAMLFound | null>(null)

  async function run() {
    setBusy(true)
    setError('')
    setFound(null)
    try {
      setFound((await api.testSAML(organisation)).found)
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsCard title="Test" line="Reads the provider's metadata.">
      <SettingRow label="Metadata">
        <Button variant="primary" isDisabled={busy} onPress={() => void run()}>
          {busy ? 'Testing…' : 'Test'}
        </Button>
      </SettingRow>
      {(error || found) && (
        <SettingBlock>
          {error && (
            <TestResult ok={false} title="Not read">
              <span>{error}</span>
            </TestResult>
          )}
          {found && (
            <TestResult ok title="Provider found">
              <FoundValues found={found} />
            </TestResult>
          )}
        </SettingBlock>
      )}
    </SettingsCard>
  )
}

/** Test: fetches the provider's discovery document now and shows what it names. */
function Discovery({ organisation }: { organisation?: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [found, setFound] = useState<SSOEndpoints | null>(null)

  async function run() {
    setBusy(true)
    setError('')
    setFound(null)
    try {
      setFound((await api.testSSO(organisation)).endpoints)
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsCard title="Test" line="Reads the provider's discovery document.">
      <SettingRow label="Discovery">
        <Button variant="primary" isDisabled={busy} onPress={() => void run()}>
          {busy ? 'Testing…' : 'Test'}
        </Button>
      </SettingRow>
      {(error || found) && (
        <SettingBlock>
          {error && (
            <TestResult ok={false} title="Not reached">
              <span>{error}</span>
            </TestResult>
          )}
          {found && (
            <TestResult ok title="Provider found">
              <Values
                items={[
                  { label: 'Issuer', value: mono(found.issuer) },
                  { label: 'Sign-in', value: mono(found.authorizationEndpoint) },
                  { label: 'Token', value: mono(found.tokenEndpoint) },
                  { label: 'Keys', value: mono(found.jwksUri) },
                  {
                    label: 'Sign-out',
                    value: found.endSessionEndpoint ? mono(found.endSessionEndpoint) : <span className="set-muted">None</span>,
                  },
                  { label: 'Algorithms', value: found.algorithms.join(', ') },
                ]}
              />
            </TestResult>
          )}
        </SettingBlock>
      )}
    </SettingsCard>
  )
}
